import { useState, useEffect, useCallback } from 'react';
import deviceService from '../services/deviceService';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook personalizado para la gestión completa de Dispositivos.
 * Sigue el mismo patrón que useUsers y useContacts.
 */
export const useDevices = () => {
  // Estados de datos
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación - NUEVO
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados de operaciones CRUD
  const [operationStatus, setOperationStatus] = useState(OPERATION_STATUS.IDLE);
  const [operationError, setOperationError] = useState(null);
  const [operatingDeviceName, setOperatingDeviceName] = useState('');
  const [isEditingMode, setIsEditingMode] = useState(false);

  /**
   * Carga la lista de dispositivos y las estadísticas desde el backend con paginación.
   */
  const loadDevices = useCallback(async (page = 1, limit = 10, search = '', searchType = 'device') => {
    try {
      setLoading(true);
      setError(null);
      
      const [devicesResponse, statsResponse] = await Promise.all([
        deviceService.getAllDevices({ 
          page, 
          limit, 
          search: search || undefined,
          search_type: searchType 
        }),
        deviceService.getDeviceStats()
      ]);
      
      if (devicesResponse.success) {
        // La nueva estructura del backend tiene los devices en data.devices
        setDevices(devicesResponse.data?.devices || devicesResponse.data || []);
        
        // Capturar información de paginación del backend
        if (devicesResponse.data?.pagination) {
          setPagination(devicesResponse.data.pagination);
        }
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data || { total: 0, enabled: 0, disabled: 0 });
      }
      
    } catch (err) {
      console.error('Error cargando dispositivos:', err);
      setError('No se pudieron cargar los datos de los dispositivos.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambiar a una página específica
   */
  const goToPage = useCallback(async (page, search = '', searchType = 'device') => {
    await loadDevices(page, pagination.limit, search, searchType);
  }, [loadDevices, pagination.limit]);

  /**
   * Ir a la página siguiente
   */
  const nextPage = useCallback(async (search = '', searchType = 'device') => {
    if (pagination.hasNext) {
      await goToPage(pagination.page + 1, search, searchType);
    }
  }, [pagination.hasNext, pagination.page, goToPage]);

  /**
   * Ir a la página anterior
   */
  const prevPage = useCallback(async (search = '', searchType = 'device') => {
    if (pagination.hasPrev) {
      await goToPage(pagination.page - 1, search, searchType);
    }
  }, [pagination.hasPrev, pagination.page, goToPage]);

  /**
   * Cambiar el número de elementos por página
   */
  const changePageSize = useCallback(async (newLimit, search = '', searchType = 'device') => {
    await loadDevices(1, newLimit, search, searchType); // Reiniciar a página 1 con nuevo tamaño
  }, [loadDevices]);

  /**
   * Ejecuta una operación de dispositivo con UX optimizada
   */
  const executeDeviceOperation = useCallback(async (operation, deviceData, isEdit = false) => {
    setOperatingDeviceName(deviceData.nombre);
    setOperationStatus(OPERATION_STATUS.LOADING);
    setIsEditingMode(isEdit);
    setOperationError(null);

    const startTime = Date.now();

    try {
      const response = await operation();

      // Asegurar tiempo mínimo de loading para UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.success) {
        setOperationStatus(OPERATION_STATUS.SUCCESS);
        await loadDevices(); // Recargar lista
        return { success: true };
      } else {
        setOperationStatus(OPERATION_STATUS.ERROR);
        setOperationError(response.message || 'Ocurrió un error desconocido.');
        return { success: false, message: response.message };
      }

    } catch (error) {
      console.error('Error en operación de dispositivo:', error);
      setOperationStatus(OPERATION_STATUS.ERROR);
      setOperationError(error.message || 'Ocurrió un error de conexión.');
      return { success: false, message: error.message };
    }
  }, [loadDevices]);

  /**
   * Crea un nuevo dispositivo
   */
  const createDevice = useCallback(async (deviceData) => {
    return executeDeviceOperation(
      () => deviceService.createDevice(deviceData),
      deviceData,
      false
    );
  }, [executeDeviceOperation]);

  /**
   * Actualiza un dispositivo existente
   */
  const updateDevice = useCallback(async (deviceId, deviceData) => {
    return executeDeviceOperation(
      () => deviceService.updateDevice(deviceId, deviceData),
      deviceData,
      true
    );
  }, [executeDeviceOperation]);

  /**
   * Elimina un dispositivo
   */
  const deleteDevice = useCallback(async (deviceId, deviceData = { nombre: 'Dispositivo' }) => {
    return executeDeviceOperation(
      () => deviceService.deleteDevice(deviceId),
      deviceData,
      false
    );
  }, [executeDeviceOperation]);

  /**
   * Resetea el estado de operación
   */
  const resetOperationState = useCallback(() => {
    setOperationStatus(OPERATION_STATUS.IDLE);
    setOperationError(null);
    setOperatingDeviceName('');
    setIsEditingMode(false);
  }, []);

  // Cargar datos al montar el hook
  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  return {
    // Estados
    devices,
    stats,
    loading,
    error,
    operationStatus,
    operationError,
    operatingDeviceName,
    isEditingMode,
    pagination, // Exponer el estado de paginación
    
    // Funciones
    loadDevices,
    createDevice,
    updateDevice,
    deleteDevice,
    resetOperationState,
    goToPage, // Exponer funciones de paginación
    nextPage,
    prevPage,
    changePageSize,
  };
}; 